import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import { EASE_OUT } from '../../utils/motion';
import type { NavItem } from '../../types/content';
export function AboutDropdown({
  item
}: {
  item: NavItem;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const hoverTimer = useRef<number>();
  const location = useLocation();
  const isActive = location.pathname.startsWith('/about');
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);
  useEffect(() => () => window.clearTimeout(hoverTimer.current), []);
  const onPointerEnter = (event: React.PointerEvent) => {
    if (event.pointerType !== 'mouse') return;
    window.clearTimeout(hoverTimer.current);
    setOpen(true);
  };
  const onPointerLeave = (event: React.PointerEvent) => {
    if (event.pointerType !== 'mouse') return;
    hoverTimer.current = window.setTimeout(() => setOpen(false), 180);
  };
  const onBlur = (event: React.FocusEvent) => {
    if (!wrapperRef.current?.contains(event.relatedTarget as Node)) setOpen(false);
  };
  return <div ref={wrapperRef} className="relative" onPointerEnter={onPointerEnter} onPointerLeave={onPointerLeave} onBlur={onBlur} data-id="element-238" data-mp="163zsf861h96z">
      <div className="flex items-center" data-id="element-239" data-mp="1cksvumqzhdqb">
        <Link to={item.to} className={`relative px-3 py-2 font-display text-[15px] font-semibold uppercase tracking-[0.08em] transition-colors duration-150 ${isActive ? 'text-white' : 'text-steel-200 hover:text-white'}`} data-id="element-240" data-mp="1cjq4kdpr199v">
          {item.label}
          {isActive && <span className="absolute inset-x-3 -bottom-0.5 h-[3px] bg-brand-red" aria-hidden="true" data-id="element-241" data-mp="ou5mdx1f6g1n" />}
        </Link>
        <button ref={toggleRef} type="button" aria-expanded={open} aria-controls="about-menu" aria-label="Show About Us pages" onClick={() => setOpen(value => !value)} className="-ml-2 flex h-9 w-7 items-center justify-center text-steel-300 transition-colors duration-150 hover:text-white" data-id="element-242" data-mp="to9rsbv1eg0m">
          <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ease-out ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-243" data-mp="av5ywspn8772" />
        </button>
      </div>

      <AnimatePresence data-id="element-244" data-mp="3ihrq3zwir5v3">
        {open && <motion.div id="about-menu" initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.18,
        ease: EASE_OUT
      }} className="absolute left-0 top-full z-50 w-[390px] pt-3" data-id="element-245" data-mp="ac29zsz0fgk">
            <ul className="border-t-[3px] border-brand-red bg-charcoal-900 py-2 shadow-2xl shadow-black/60 ring-1 ring-white/10" data-id="element-246" data-mp="5agh7le8d24o">
              {item.children?.map(child => <li key={child.to} data-id="element-247" data-mp="ezqdldg3x6yl">
                  <NavLink to={child.to} onClick={() => setOpen(false)} className={({
              isActive: childActive
            }) => `block border-l-[3px] px-5 py-3.5 transition-colors duration-150 hover:bg-charcoal-800 ${childActive ? 'border-brand-red bg-charcoal-800' : 'border-transparent'}`} data-id="element-248" data-mp="f0szf6ykoj7n">
                    <span className="block font-display text-[17px] font-semibold uppercase tracking-wide text-white" data-id="element-249" data-mp="2s66dzitg63vd">
                      {child.label}
                    </span>
                    {child.description && <span className="mt-0.5 block text-sm text-steel-400" data-id="element-250" data-mp="1ziamqtmnd45z">{child.description}</span>}
                  </NavLink>
                </li>)}
            </ul>
          </motion.div>}
      </AnimatePresence>
    </div>;
}